import * as React from 'react'
import {ReactElement} from 'react'
import {NatApplicationRepo} from '../repository/NatApplicationRepo'
import {
    NatContactAddressOnEmergencyInfo,
    NatResidentContactAddressOnEmergencyLocationState,
} from './NatResidentApplicationInterface'
import NatCurrentDateTimeRepo from '../../currentDateTime/repository/NatCurrentDateTimeRepo'
import NatCondominiumRepo from '../../condominium/repository/NatCondominiumRepo'
import {Nullable} from '../../../../app/TypeHelpers'
import {RESIDENCE_STYLE} from '../../../../CodeMaster'
import NatResidentContactAddressOnEmergencyConfirm from './resident/NatResidentContactAddressOnEmergencyConfirm'
import {BlockSpacer} from '../../../../styledComponents/ResponsiveConvenience'
import {DisplayItemDividerForPageTop} from '../../../../styledComponents/DisplayItem'
import {
    CenterPlainLinkStyledButton,
    PlainLinkStyledBackButton,
    PrimaryStyledButton,
} from '../../../../styledComponents/Button'
import {
    NatFamilyStructureRequest,
    NatPutContactAddressOnEmergencyRequest,
    NatPutContactAddressOnEmergencyResponse,
} from '../repository/NatPutContactAddressOnEmergency'
import {Box} from '@mui/material'
import {PageTitle} from '../common/resident/NatResidentApplicationsCommon'
import {
    createResidentWithErrorHandlingPage,
    ResidentWithErrorHandlingPagePropsSet,
} from '../../../../components/shared/hoc/createResidentWithErrorHandlingPage'
import {Location} from 'react-router-dom'

interface Props {
    condominiumCd: string
    applicationRepo: NatApplicationRepo
    condominiumRepo: NatCondominiumRepo
    currentDateTimeRepo: NatCurrentDateTimeRepo
    location: Location<NatResidentContactAddressOnEmergencyLocationState>
}

class NatResidentContactAddressOnEmergencyConfirmPage
    extends React.Component<Props & ResidentWithErrorHandlingPagePropsSet> {

    constructor(props: Props & ResidentWithErrorHandlingPagePropsSet) {
        super(props)

        this.handleRegisterButtonClick = this.handleRegisterButtonClick.bind(this)
        this.handleBackLinkOnClick = this.handleBackLinkOnClick.bind(this)
    }

    async handleRegisterButtonClick(): Promise<void> {
        await this.props.eventEmitter.showLoading()
        const {applicationRepo, condominiumCd} = this.props
        const locationState: NatContactAddressOnEmergencyInfo = this.props.location.state.applyInfo
        const {
            applicantFamilyName, applicantFirstName,
            contactAddressOnEmergency, absenceContactAddress, familyStructureList,
            rentAdminContactAddress, rentAdminContactAddressFlag, occupancyDate,
        } = locationState
        const residenceStyle = this.props.location.state.residenceStyle

        const familyStructureRequest: NatFamilyStructureRequest[] = familyStructureList.map(family => {
            const birthDate = (family.birthYear || family.birthMonth || family.birthDay) ?
                `${family.birthYear}-${family.birthMonth}-${family.birthDay}` : ''
            return {
                index: family.index,
                relationship: family.relationship,
                familyName: family.familyName,
                firstName: family.firstName,
                familyNameKana: family.familyNameKana,
                firstNameKana: family.firstNameKana,
                birthDate: birthDate,
                workAddress: family.workAddress,
            }
        })

        let request: Nullable<NatPutContactAddressOnEmergencyRequest> = null

        switch (residenceStyle) {
            case RESIDENCE_STYLE.UNIT_OWNER.value:
                request = {
                    residenceStyle: residenceStyle,
                    applicantFamilyName: applicantFamilyName,
                    applicantFirstName: applicantFirstName,
                    occupancyDate: occupancyDate,
                    contactAddressOnEmergency: {
                        fixedPhoneNumber: contactAddressOnEmergency.fixedPhoneNumber,
                        mobilePhoneNumber: contactAddressOnEmergency.mobilePhoneNumber,
                    },
                    absenceContactAddress: absenceContactAddress,
                    familyStructureList: familyStructureRequest,
                    confirmed: true,
                }
                break
            case RESIDENCE_STYLE.APPROPRIATOR_AND_TENANT.value:
                request = {
                    residenceStyle: residenceStyle,
                    applicantFamilyName: applicantFamilyName,
                    applicantFirstName: applicantFirstName,
                    occupancyDate: occupancyDate,
                    contactAddressOnEmergency: {
                        fixedPhoneNumber: contactAddressOnEmergency.fixedPhoneNumber,
                        mobilePhoneNumber: contactAddressOnEmergency.mobilePhoneNumber,
                    },
                    absenceContactAddress: absenceContactAddress,
                    rentAdminContactAddress: rentAdminContactAddress,
                    rentAdminContactAddressFlag: rentAdminContactAddressFlag,
                    familyStructureList: familyStructureRequest,
                    confirmed: true,
                }
                break
            case RESIDENCE_STYLE.EXTERNAL_UNIT_OWNER.value:
                request = {
                    residenceStyle: residenceStyle,
                    applicantFamilyName: applicantFamilyName,
                    applicantFirstName: applicantFirstName,
                    contactAddressOnEmergency: contactAddressOnEmergency,
                    absenceContactAddress: absenceContactAddress,
                    rentAdminContactAddress: rentAdminContactAddress,
                    rentAdminContactAddressFlag: rentAdminContactAddressFlag,
                    confirmed: true,
                }
                break
        }

        if (!request) {
            return
        }

        try {
            const response: Nullable<NatPutContactAddressOnEmergencyResponse> = await this.props.asyncCallFunction(
                applicationRepo.applyContactAddressOnEmergency.bind(
                    applicationRepo, condominiumCd, request),
                () => {
                    this.props.navigate(`/resident/error`, {replace: true})
                },
            )
            if (response && response.requestError === null) {
                this.props.navigate(
                    `/resident/condominiums/${this.props.condominiumCd}/applications/complete`,
                    {replace: true},
                )
            }
        } finally {
            this.props.eventEmitter.closeLoading()
        }
    }

    handleBackLinkOnClick(): void {
        const locationState: NatResidentContactAddressOnEmergencyLocationState = this.props.location.state
        this.props.navigate(`/resident/condominiums/${this.props.condominiumCd}/applications/contactAddressOnEmergencies/input`, {
            replace: true,
            state: JSON.parse(JSON.stringify(locationState)),
        })
    }

    render(): ReactElement {
        return (
            <>
                <PlainLinkStyledBackButton onClick={this.handleBackLinkOnClick}>戻る</PlainLinkStyledBackButton>
                <BlockSpacer height={'16px'}/>
                <DisplayItemDividerForPageTop/>
                <BlockSpacer height={'30px'}/>
                <PageTitle>
                    申請内容確認
                </PageTitle>
                <Box height={'20px'}/>
                <NatResidentContactAddressOnEmergencyConfirm
                    residenceStyle={this.props.location.state.residenceStyle}
                    {...this.props.location.state.applyInfo}
                />
                <BlockSpacer height={'30px'}/>
                <PrimaryStyledButton onClick={this.handleRegisterButtonClick}>
                    申請を提出する
                </PrimaryStyledButton>
                <BlockSpacer height={'20px'}/>
                <CenterPlainLinkStyledButton onClick={this.handleBackLinkOnClick}>修正する</CenterPlainLinkStyledButton>
                <BlockSpacer height={'15px'}/>
            </>
        )
    }
}

export default createResidentWithErrorHandlingPage(NatResidentContactAddressOnEmergencyConfirmPage)
